import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { cosechaLabel } from '../components/common/cosecha';
import { Destino, LoteAsignacion, energiaGerminacion, poderGerminativo } from '../models/lot-asignacion';
import { AnalisisMock, SolicitudMock } from '../models/trazabilidad.model';
import { AsignacionesService } from './asignaciones.service';

/** Lote del tablero de asignación con totales por lote. */
export interface LoteTablero {
  id: string;
  planta: number;
  cosecha: string;
  variedad: string;
  calibre: string;
  pg: number | null;
  eg: number | null;
  bolsones: number;
  kg: number;
  /** Ids de los destinos comprometidos (`DestinoTablero.id`). */
  destinos: string[];
  analisis: AnalisisMock[];
  solicitud?: SolicitudMock;
  /** Claves para pedir los bolsones del lote (`BolsonesFiltro`). */
  productCode: number;
  harvestYear: number;
  groupLot: string;
  /** Área (curadora) del lote, para `PArea_Codigo` al comprometer. */
  areaCode: number;
}

/** Destino tal como lo muestra el tablero. */
export interface DestinoTablero {
  id: string;
  nombre: string;
  corto: string;
  color: string;
}

/** Color de los destinos que llegan de la API, que no definen uno propio. */
export const COLOR_DESTINO_NEUTRO = 'var(--mat-sys-outline)';

/** Lotes y destinos del tablero de asignación. */
export abstract class TableroDataService {
  abstract lotes(cosecha: number): Observable<LoteTablero[]>;
  abstract destinos(): Observable<DestinoTablero[]>;
}

/** Toneladas a kg, redondeado a kg enteros. */
const tonsToKg = (t: number) => Math.round(t * 1000);

export function toLoteTablero(lote: LoteAsignacion): LoteTablero {
  const eg = energiaGerminacion(lote);
  return {
    id: lote.batchCode,
    planta: lote.plantCode,
    cosecha: cosechaLabel(lote.harvestYear),
    variedad: lote.variety,
    calibre: lote.caliber,
    pg: poderGerminativo(lote),
    eg: eg || null,
    bolsones: lote.bagCount,
    kg: tonsToKg(lote.stock),
    destinos: lote.committed > 0 ? [String(lote.committed)] : [],
    analisis: lote.analysis.map((a) => ({ nombre: a.description, valor: String(a.result), fecha: a.date })),
    productCode: lote.productCode,
    harvestYear: lote.harvestYear,
    groupLot: lote.groupLot,
    areaCode: lote.areaCode,
  };
}

export function toDestinoTablero(destino: Destino): DestinoTablero {
  return { id: String(destino.id), nombre: destino.cliente, corto: destino.nombre, color: COLOR_DESTINO_NEUTRO };
}

/** Implementación real sobre `AsignacionesService` (`/lot-assignments/*`). */
@Injectable()
export class TableroApiService extends TableroDataService {
  private readonly asignaciones = inject(AsignacionesService);

  lotes(cosecha: number): Observable<LoteTablero[]> {
    return this.asignaciones.getLotes({ harvestYear: cosecha }).pipe(map((lotes) => lotes.map(toLoteTablero)));
  }

  destinos(): Observable<DestinoTablero[]> {
    return this.asignaciones.getDestinos().pipe(map((destinos) => destinos.map(toDestinoTablero)));
  }
}
